import React, { useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import Typography from '@mui/material/Typography/index';
import InputAdornment from '@mui/material/InputAdornment/index';
import IconButton from '@mui/material/IconButton/index';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Accordion from '@mui/material/Accordion/index';
import AccordionSummary from '@mui/material/AccordionSummary/index';
import AccordionActions from '@mui/material/AccordionActions/index';
import Divider from '@mui/material/Divider/index';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { reduxFormResolver } from '#helper/rhf-resolver';
import RHFTextField from '#component/form/rhf/rhf-text-field';
import { updateUserSettings } from '#action/user/settings';
import validate from './validate-password';
import { StyledButton, StyledExpansionPanelDetails } from './styles';

const passwordHelperText = 'For security, your password must be at least 8 characters long. It must contain letters, numbers and at least one special character.';

const UserPasswordForm = props => {
  const {
    history,
    title,
    expanded = false,
    handleFormSubmit: propsHandleFormSubmit,
    hideCancelButton = false,
    user,
    dispatch
  } = props;

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const methods = useForm({
    resolver: reduxFormResolver(validate),
    defaultValues: { password: '', confirmPassword: '' }
  });

  const { handleSubmit, formState: { isDirty, isSubmitting }, reset } = methods;

  const defaultHandleFormSubmit = async values => {
    const { password } = values;

    await dispatch(
      updateUserSettings(
        user.id,
        {
          password
        }
      )
    );

    reset();

    if (user.directProjects.length) {
      history.push(user.defaultPage || '/device');
    }
  };

  const onSubmit = propsHandleFormSubmit || defaultHandleFormSubmit;

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        autoComplete="off"
        style={{ width: '100%' }}
      >
        <Accordion defaultExpanded={expanded} elevation={0}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" gutterBottom>
              {title}
            </Typography>
          </AccordionSummary>
          <StyledExpansionPanelDetails>
            <RHFTextField
              name="password"
              label="Password"
              variant="outlined"
              fullWidth
              margin="normal"
              type={showPassword ? 'text' : 'password'}
              info={passwordHelperText}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Toggle password visibility"
                      onClick={() => setShowPassword(s => !s)}
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <RHFTextField
              name="confirmPassword"
              label="Confirm password"
              variant="outlined"
              fullWidth
              margin="normal"
              type={showConfirmPassword ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Toggle password visibility"
                      onClick={() => setShowConfirmPassword(s => !s)}
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </StyledExpansionPanelDetails>
          <Divider />
          <AccordionActions>
            {!hideCancelButton && (
              <StyledButton
                variant="outlined"
                onClick={() => {
                  history.goBack();
                }}
              >
                Cancel
              </StyledButton>)}
            <StyledButton
              disabled={!isDirty || isSubmitting}
              type="submit"
              variant="contained"
              color="secondary"
            >
              Save
            </StyledButton>
          </AccordionActions>
        </Accordion>
      </form>
    </FormProvider>
  );
};

export default UserPasswordForm;
